import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Autocomplete,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  Paper,
  Radio,
  Grid,
} from '@mui/material';
import {
  UploadFile,
  Sync,
  Refresh,
  Block,
  LinkOff,
  ReceiptLong,
  CheckCircle,
  WarningAmber,
  InsertDriveFile,
  AccountBalance,
  FilterList,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  TableEmptyState,
  ErrorState,
  StatusTone,
} from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { api } from '../../services/apiClient';
import treasuryService from '../../services/treasuryService';
import { BankAccount } from '../../types/treasury';
import { formatIndianNumber } from '../../utils/currency';

/**
 * Bank Statement Import + Auto-Match — a reconciliation aid.
 *
 * ReconciliationPage (treasury) matches GL-side ledger lines to a single
 * closing balance and explicitly flags statement-line auto-matching as
 * "roadmap" (statement lines aren't stored there). This screen is that
 * roadmap item: import a real statement line-by-line via /bank-statements,
 * auto-match each line against ledger candidates, and resolve the leftovers
 * by hand. It complements ReconciliationPage rather than replacing it.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MatchStatus = 'UNMATCHED' | 'MATCHED' | 'IGNORED';

interface StatementLine {
  id: number;
  statement_date: string;
  value_date: string | null;
  description: string;
  reference: string | null;
  debit_amount: number;
  credit_amount: number;
  running_balance: number | null;
  match_status: MatchStatus;
  matched_reference_type: string | null;
  matched_reference_id: number | null;
}

interface LedgerCandidate {
  referenceType: string;
  referenceId: number;
  date: string;
  amount: number;
  side: 'DEBIT' | 'CREDIT';
  reference: string | null;
  description: string;
}

interface StatementSummary {
  totalLines?: number;
  total?: number;
  matchedCount?: number;
  unmatchedCount?: number;
  ignoredCount?: number;
  matchedNet?: number;
  unmatchedNet?: number;
  difference?: number;
  linkedReconciliation?: { id: number; status: string } | null;
  byStatus?: Record<string, { count?: number; net?: number }>;
  [key: string]: any;
}

const MATCH_STATUS_TONE: Record<MatchStatus, StatusTone> = {
  MATCHED: 'success',
  UNMATCHED: 'warning',
  IGNORED: 'neutral',
};

const formatDisplayDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Tolerant extraction — the summary shape is described loosely ("totals by
// status"), so this reads a handful of plausible key spellings rather than
// assuming one exact contract. Falls back to null (rendered as an em dash by
// StatCard) instead of fabricating a number.
const summaryCount = (summary: StatementSummary | null, status: MatchStatus): number | null => {
  if (!summary) return null;
  const camel = status === 'MATCHED' ? 'matchedCount' : status === 'UNMATCHED' ? 'unmatchedCount' : 'ignoredCount';
  const direct = summary[camel];
  if (typeof direct === 'number') return direct;
  const byStatus = summary.byStatus?.[status];
  if (byStatus && typeof byStatus.count === 'number') return byStatus.count;
  return null;
};

const BankStatementImportPage: React.FC = () => {
  const colors = useControlRoomColors();

  // Bank accounts -----------------------------------------------------------
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(true);
  const [selectedBank, setSelectedBank] = useState<BankAccount | null>(null);

  useEffect(() => {
    treasuryService
      .listBanks()
      .then((r) => setBankAccounts((r.accounts || []).filter((b) => b.is_active)))
      .catch(() => setBankAccounts([]))
      .finally(() => setBankAccountsLoading(false));
  }, []);

  const bankAccountId = selectedBank?.id ?? null;

  // Statement lines -----------------------------------------------------------
  const [lines, setLines] = useState<StatementLine[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<MatchStatus | ''>('');

  const fetchLines = useCallback(() => {
    if (!bankAccountId) {
      setLines([]);
      return;
    }
    setLinesLoading(true);
    setLinesError(null);
    api
      .get<{ lines: StatementLine[] }>('/bank-statements/lines', {
        params: { bankAccountId, matchStatus: statusFilter || undefined },
      })
      .then((r) => setLines(r.lines || []))
      .catch((err: any) => setLinesError(err.message || 'Failed to load statement lines'))
      .finally(() => setLinesLoading(false));
  }, [bankAccountId, statusFilter]);

  // Summary ---------------------------------------------------------------
  const [summary, setSummary] = useState<StatementSummary | null>(null);

  const fetchSummary = useCallback(() => {
    if (!bankAccountId) {
      setSummary(null);
      return;
    }
    api
      .get<StatementSummary>('/bank-statements/summary', { params: { bankAccountId } })
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [bankAccountId]);

  useEffect(() => {
    fetchLines();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccountId, statusFilter]);

  const refreshAll = () => {
    fetchLines();
    fetchSummary();
  };

  // Toast -------------------------------------------------------------------
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Import dialog -------------------------------------------------------------
  const [importOpen, setImportOpen] = useState(false);
  const [importTab, setImportTab] = useState<0 | 1>(0);
  const [csvText, setCsvText] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ importBatch: string; insertedCount: number } | null>(null);

  const openImportDialog = () => {
    setImportTab(0);
    setCsvText('');
    setImportFile(null);
    setImportError(null);
    setImportResult(null);
    setImportOpen(true);
  };

  const closeImportDialog = () => {
    if (importing) return;
    setImportOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportFile(file);
    setImportError(null);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => setImportError('Failed to read the selected file.');
    reader.readAsText(file);
  };

  const submitImport = async () => {
    if (!bankAccountId || !csvText.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      const res = await api.post<{ importBatch: string; insertedCount: number }>('/bank-statements/import', {
        bankAccountId,
        csv: csvText,
      });
      setImportResult(res);
      setToast({ message: `Imported ${res.insertedCount} line(s) — batch ${res.importBatch}`, severity: 'success' });
      refreshAll();
    } catch (err: any) {
      setImportError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Auto-match ----------------------------------------------------------------
  const [autoMatching, setAutoMatching] = useState(false);

  const runAutoMatch = async () => {
    if (!bankAccountId) return;
    setAutoMatching(true);
    try {
      const res = await api.post<{ matched: number; unmatched: number; ambiguous: number; total: number }>(
        '/bank-statements/auto-match',
        { bankAccountId }
      );
      setToast({
        message: `Auto-match complete — ${res.matched} matched, ${res.unmatched} unmatched, ${res.ambiguous} ambiguous`,
        severity: 'success',
      });
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Auto-match failed', severity: 'error' });
    } finally {
      setAutoMatching(false);
    }
  };

  // Match dialog ----------------------------------------------------------------
  const [matchTarget, setMatchTarget] = useState<StatementLine | null>(null);
  const [candidates, setCandidates] = useState<LedgerCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<LedgerCandidate | null>(null);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    if (!matchTarget || !bankAccountId) return;
    setCandidatesLoading(true);
    setCandidatesError(null);
    setSelectedCandidate(null);
    api
      .get<{ candidates: LedgerCandidate[] }>('/bank-statements/ledger-candidates', { params: { bankAccountId } })
      .then((r) => setCandidates(r.candidates || []))
      .catch((err: any) => setCandidatesError(err.message || 'Failed to load ledger candidates'))
      .finally(() => setCandidatesLoading(false));
  }, [matchTarget, bankAccountId]);

  // A statement withdrawal (debit) is a credit to the bank GL account, and a
  // statement deposit (credit) is a debit to it — so the "compatible side" is
  // the opposite of the statement leg. Amount is matched with a small
  // tolerance for paisa rounding. If nothing matches on side, fall back to an
  // amount-only match rather than showing an empty list on a guess.
  const lineAmount = matchTarget ? (matchTarget.debit_amount > 0 ? matchTarget.debit_amount : matchTarget.credit_amount) : 0;
  const lineSide: 'DEBIT' | 'CREDIT' | null = matchTarget ? (matchTarget.debit_amount > 0 ? 'CREDIT' : 'DEBIT') : null;
  const AMOUNT_EPSILON = 0.01;

  const amountMatches = useMemo(
    () => candidates.filter((c) => Math.abs(Number(c.amount) - lineAmount) < AMOUNT_EPSILON),
    [candidates, lineAmount]
  );
  const strictCandidates = useMemo(
    () => amountMatches.filter((c) => c.side === lineSide),
    [amountMatches, lineSide]
  );
  const displayCandidates = strictCandidates.length > 0 ? strictCandidates : amountMatches;
  const usingFallback = strictCandidates.length === 0 && displayCandidates.length > 0;

  const confirmMatch = async () => {
    if (!matchTarget || !selectedCandidate) return;
    setMatching(true);
    try {
      await api.post(`/bank-statements/lines/${matchTarget.id}/match`, {
        referenceType: selectedCandidate.referenceType,
        referenceId: selectedCandidate.referenceId,
      });
      setToast({ message: 'Line matched', severity: 'success' });
      setMatchTarget(null);
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to match line', severity: 'error' });
    } finally {
      setMatching(false);
    }
  };

  // Unmatch / ignore ----------------------------------------------------------------
  const [actingOnId, setActingOnId] = useState<number | null>(null);

  const unmatchLine = async (line: StatementLine) => {
    setActingOnId(line.id);
    try {
      await api.post(`/bank-statements/lines/${line.id}/unmatch`);
      setToast({ message: 'Line unmatched', severity: 'success' });
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to unmatch line', severity: 'error' });
    } finally {
      setActingOnId(null);
    }
  };

  const ignoreLine = async (line: StatementLine) => {
    setActingOnId(line.id);
    try {
      await api.post(`/bank-statements/lines/${line.id}/ignore`);
      setToast({ message: 'Line ignored', severity: 'success' });
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to ignore line', severity: 'error' });
    } finally {
      setActingOnId(null);
    }
  };

  // Stat card values ----------------------------------------------------------------
  const totalLines = summary ? summary.totalLines ?? summary.total ?? null : null;
  const matchedCount = summaryCount(summary, 'MATCHED');
  const unmatchedCount = summaryCount(summary, 'UNMATCHED');
  const difference = summary ? summary.difference ?? null : null;

  const csvLineCount = csvText ? csvText.split(/\r?\n/).filter((l) => l.trim()).length : 0;

  return (
    <Box>
      <PageHeader
        title="Bank Statement Import"
        subtitle="Import a bank statement, auto-match it against the ledger, and resolve exceptions manually."
        actions={
          <>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={refreshAll} disabled={!bankAccountId || linesLoading}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Sync />}
              disabled={!bankAccountId || autoMatching}
              onClick={runAutoMatch}
            >
              {autoMatching ? 'Matching…' : 'Auto-Match'}
            </Button>
            <Button variant="contained" size="small" startIcon={<UploadFile />} onClick={openImportDialog}>
              Import Statement
            </Button>
          </>
        }
      />

      <FilterBar>
        <Autocomplete
          options={bankAccounts}
          getOptionLabel={(option) => `${option.account_name} · ${option.bank_name}`}
          value={selectedBank}
          onChange={(_, newValue) => setSelectedBank(newValue)}
          loading={bankAccountsLoading}
          sx={{ minWidth: 300, flexGrow: { xs: 1, md: 0 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Bank Account"
              size="small"
              placeholder="Select a bank account"
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <AccountBalance fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.account_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.bank_name} · {option.account_number}
                </Typography>
              </Box>
            </li>
          )}
        />

        <TextField
          select
          size="small"
          label="Match Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as MatchStatus | '')}
          sx={{ minWidth: 170 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="UNMATCHED">Unmatched</MenuItem>
          <MenuItem value="MATCHED">Matched</MenuItem>
          <MenuItem value="IGNORED">Ignored</MenuItem>
        </TextField>

        <FilterSpacer />

        {summary?.linkedReconciliation && (
          <Tooltip title="This bank account has an in-progress or completed reconciliation on the treasury side">
            <Box>
              <TypePill
                label={`Reconciliation #${summary.linkedReconciliation.id} · ${summary.linkedReconciliation.status}`}
                tint={colors.accent}
              />
            </Box>
          </Tooltip>
        )}
      </FilterBar>

      {!bankAccountId ? (
        <TableSurface>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <AccountBalance sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Select a bank account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a bank account above to view its imported statement lines.
            </Typography>
          </Box>
        </TableSurface>
      ) : (
        <>
          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="Total Lines" value={totalLines} icon={<ReceiptLong />} color={colors.cyan} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="Matched" value={matchedCount} icon={<CheckCircle />} color={colors.good} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="Unmatched" value={unmatchedCount} icon={<WarningAmber />} color={colors.warn} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                title="Difference"
                value={difference === null ? null : formatIndianNumber(difference)}
                icon={<Sync />}
                color={difference === 0 ? colors.good : colors.warn}
              />
            </Grid>
          </Grid>

          {linesError ? (
            <TableSurface>
              <ErrorState message={linesError} onRetry={fetchLines} />
            </TableSurface>
          ) : (
            <TableSurface>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: 110 }}>Date</TableCell>
                      <TableCell sx={headerCellSx}>Description</TableCell>
                      <TableCell sx={{ ...headerCellSx, width: 150 }}>Reference</TableCell>
                      <TableCell align="right" sx={{ ...headerCellSx, width: 130 }}>Debit</TableCell>
                      <TableCell align="right" sx={{ ...headerCellSx, width: 130 }}>Credit</TableCell>
                      <TableCell sx={{ ...headerCellSx, width: 130 }}>Status</TableCell>
                      <TableCell sx={{ ...headerCellSx, width: 220 }}>Matched Reference</TableCell>
                      <TableCell align="right" sx={{ ...headerCellSx, width: 150 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {linesLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                          <CircularProgress size={26} />
                        </TableCell>
                      </TableRow>
                    ) : lines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ border: 0 }}>
                          <TableEmptyState
                            title="No statement lines"
                            hint="Import a statement to see lines here, or widen the match status filter."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      lines.map((line) => (
                        <TableRow key={line.id} sx={bodyRowSx}>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {formatDisplayDate(line.statement_date)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{line.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                              {line.reference || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                            >
                              {line.debit_amount ? formatIndianNumber(line.debit_amount) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                            >
                              {line.credit_amount ? formatIndianNumber(line.credit_amount) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusDot label={line.match_status} tone={MATCH_STATUS_TONE[line.match_status]} />
                          </TableCell>
                          <TableCell>
                            {line.match_status === 'MATCHED' && line.matched_reference_type ? (
                              <TypePill
                                label={`${line.matched_reference_type} #${line.matched_reference_id}`}
                                tint={colors.accent}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {line.match_status === 'UNMATCHED' && (
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => setMatchTarget(line)}>
                                  Match
                                </Button>
                                <Tooltip title="Ignore this line — exclude it from reconciliation">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => ignoreLine(line)}
                                      disabled={actingOnId === line.id}
                                    >
                                      <Block fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            )}
                            {line.match_status === 'MATCHED' && (
                              <Tooltip title="Unmatch this line">
                                <span>
                                  <Button
                                    size="small"
                                    color="inherit"
                                    startIcon={<LinkOff fontSize="small" />}
                                    onClick={() => unmatchLine(line)}
                                    disabled={actingOnId === line.id}
                                  >
                                    Unmatch
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          )}
        </>
      )}

      {/* Import dialog ---------------------------------------------------------------- */}
      <Dialog open={importOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Statement</DialogTitle>
        <DialogContent>
          {!bankAccountId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Select a bank account before importing a statement.
            </Alert>
          )}
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
              {importError}
            </Alert>
          )}

          {importResult ? (
            <Alert severity="success" icon={<CheckCircle />}>
              Imported <strong>{importResult.insertedCount}</strong> line(s) into batch{' '}
              <strong>{importResult.importBatch}</strong>.
            </Alert>
          ) : (
            <>
              <Tabs value={importTab} onChange={(_, v) => setImportTab(v)} sx={{ mb: 2 }}>
                <Tab label="Paste CSV" />
                <Tab label="Upload File" />
              </Tabs>

              {importTab === 0 ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={10}
                  placeholder={'Date,Description,Reference,Debit,Credit,Balance\n01-Apr-2026,NEFT to Vendor,REF12345,15000,,84500'}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                  <Button component="label" variant="outlined" startIcon={<UploadFile />}>
                    Choose CSV File
                    <input hidden type="file" accept=".csv,text/csv" onChange={handleFileChange} />
                  </Button>
                  {importFile && (
                    <Typography variant="body2" sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <InsertDriveFile fontSize="small" />
                      {importFile.name} · {csvLineCount} line(s) read
                    </Typography>
                  )}
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Accepted columns (header row optional): <strong>Date, Description, Reference, Debit, Credit, Balance</strong>.
                  One transaction per line, comma-separated.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          {importResult ? (
            <Button variant="contained" onClick={closeImportDialog}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={closeImportDialog} disabled={importing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={submitImport}
                disabled={importing || !bankAccountId || !csvText.trim()}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Match dialog ---------------------------------------------------------------- */}
      <Dialog open={!!matchTarget} onClose={() => !matching && setMatchTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Match Statement Line</DialogTitle>
        <DialogContent>
          {matchTarget && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {matchTarget.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDisplayDate(matchTarget.statement_date)}
                {matchTarget.reference ? ` · Ref: ${matchTarget.reference}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                {formatIndianNumber(lineAmount)}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  {matchTarget.debit_amount > 0 ? 'Debit (out)' : 'Credit (in)'}
                </Typography>
              </Typography>
            </Paper>
          )}

          {candidatesError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCandidatesError(null)}>
              {candidatesError}
            </Alert>
          )}

          {candidatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : displayCandidates.length === 0 ? (
            <TableEmptyState
              title="No matching ledger candidates"
              hint="Try a different date range, or post the corresponding entry in the ledger first."
            />
          ) : (
            <>
              {usingFallback && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  No candidate matched on the expected side — showing all candidates with the same amount instead.
                </Alert>
              )}
              <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: 40 }} />
                      <TableCell sx={headerCellSx}>Date</TableCell>
                      <TableCell sx={headerCellSx}>Type</TableCell>
                      <TableCell sx={headerCellSx}>Reference</TableCell>
                      <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayCandidates.map((c) => {
                      const isSelected =
                        selectedCandidate?.referenceType === c.referenceType &&
                        selectedCandidate?.referenceId === c.referenceId;
                      return (
                        <TableRow
                          key={`${c.referenceType}-${c.referenceId}`}
                          hover
                          selected={isSelected}
                          onClick={() => setSelectedCandidate(c)}
                          sx={{ ...bodyRowSx, cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Radio size="small" checked={isSelected} onChange={() => setSelectedCandidate(c)} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDisplayDate(c.date)}</TableCell>
                          <TableCell>
                            <TypePill label={c.referenceType} tint={colors.purple} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {c.reference || c.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                            {formatIndianNumber(c.amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setMatchTarget(null)} disabled={matching}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!selectedCandidate || matching} onClick={confirmMatch}>
            {matching ? 'Matching…' : 'Confirm Match'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default BankStatementImportPage;
